/**
 * 表セクションの固定行の更新。
 * templateNodes を入力に、固定行の追加・挿入を行う pure function。
 */

import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getCreateNodeId } from 'src/features/templateEdit/common/createNode'
import { cloneColumnSubtree, remapSubtreeQuestionRefs } from './cellSync'
import { getTableColumnNodes, getTableTemplateRowNode } from './selector'
import { buildNewRowSectionNode } from './tableNodeBuilders'
import type { TableSectionUpdate } from './tableSection'

type TableFixedRowUpdate = TableSectionUpdate & { activeNodeId: number }

/** 固定行を末尾（テンプレート行の直前）に追加する。 */
export const appendTableFixedRow = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TableFixedRowUpdate =>
  insertTableFixedRowRelative(tableNode, templateNodes, null, 'below')

/**
 * 基準行の上下に固定行を挿入する。
 * anchorRowId が null のときは固定行の末尾（= テンプレート行の直前）へ追加する。
 */
export const insertTableFixedRowRelative = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  anchorRowId: number | null,
  position: 'above' | 'below',
): TableFixedRowUpdate => {
  const { nodes: nodesWithRow, rowId } = buildNewTableRowWithCells(
    tableNode,
    templateNodes,
  )

  const table = nodesWithRow[tableNode.id] ?? tableNode
  const childIds = [...table.nodes]

  const resolvedAnchor =
    anchorRowId ?? getTableTemplateRowNode(table, nodesWithRow).id
  const resolvedPosition = anchorRowId == null ? 'above' : position

  const anchorIndex = childIds.indexOf(resolvedAnchor)
  if (anchorIndex < 0) {
    throw new Error('基準行が見つかりません')
  }

  const insertIndex =
    resolvedPosition === 'above' ? anchorIndex : anchorIndex + 1
  childIds.splice(insertIndex, 0, rowId)

  return {
    templateNodes: {
      ...nodesWithRow,
      [tableNode.id]: { ...table, nodes: childIds },
    },
    activeNodeId: rowId,
  }
}

/**
 * 新しい固定行を作る。
 * セルは列の条件分岐・分岐質問まで複製し、追加した時点で列と同じ形にする。
 */
const buildNewTableRowWithCells = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): { nodes: TemplateNodesDict; rowId: number } => {
  // id は一度だけ採番起点を取り、ローカルにインクリメントする
  // (getCreateNodeId は全ノード走査なのでループ内では呼ばない)。
  let nextNodeId = getCreateNodeId(templateNodes)
  const allocateId = () => nextNodeId++

  const rowId = allocateId()
  const createdNodes: TemplateNodesDict = {}
  const cellIds: number[] = []
  /** 列側 uuid → この行の複製先 uuid。 */
  const rowUuidMap = new Map<string, string>()

  for (const column of getTableColumnNodes(tableNode, templateNodes)) {
    // 新しい行のセルはすべて新規採番なので、同一性は引き継がない。
    const { cellNodeId, nodes, uuidByColumnUuid } = cloneColumnSubtree(
      column,
      templateNodes,
      allocateId,
    )
    cellIds.push(cellNodeId)
    Object.assign(createdNodes, nodes)
    uuidByColumnUuid.forEach((rowLocalUuid, columnUuid) => {
      rowUuidMap.set(columnUuid, rowLocalUuid)
    })
  }

  createdNodes[rowId] = {
    ...buildNewRowSectionNode(rowId, 0),
    nodes: cellIds,
  }

  return {
    nodes: {
      ...templateNodes,
      ...remapSubtreeQuestionRefs(createdNodes, rowUuidMap),
    },
    rowId,
  }
}
